import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock, Database, Eye, Trash2, Share2 } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F9F8 100%)' }}>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#333' }}>Privacy Policy</h1>
            <p className="text-gray-500 text-sm">Last updated: December 2025</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card prose prose-sm max-w-none"
        >
          <div className="flex items-center gap-2 mb-6 p-4 rounded-xl" style={{ backgroundColor: '#f0f9f8' }}>
            <Lock size={24} style={{ color: '#A2DAD3' }} />
            <p className="text-sm m-0">Your privacy and the security of your health data are our top priorities.</p>
          </div>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>1. Introduction</h2>
          <p className="text-gray-700 mb-4">
            Annie Cares ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains 
            how we collect, use, disclose, and safeguard your information when you use our health tracking application. 
            Please read this policy carefully to understand our practices regarding your personal data.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 flex items-center gap-2" style={{ color: '#A2DAD3' }}>
            <Database size={20} />
            2. Information We Collect
          </h2>
          <p className="text-gray-700 mb-2">We collect the following types of information:</p>
          
          <h3 className="text-lg font-medium mt-4 mb-2">Personal Information</h3>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li>Name and email address (for account creation)</li>
            <li>Password (stored in encrypted form)</li>
            <li>Subscription and billing information</li>
          </ul>

          <h3 className="text-lg font-medium mt-4 mb-2">Health Information</h3>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li>Patient profiles (name, relationship, date of birth, health conditions)</li>
            <li>Health card/insurance numbers</li>
            <li>Symptom logs and severity ratings</li>
            <li>Medication schedules and tracking data</li>
            <li>Blood pressure and blood sugar readings</li>
            <li>Hydration and nutrition data</li>
            <li>Doctor appointments and medical notes</li>
            <li>Critical health events</li>
            <li>Uploaded images (prescriptions, readings, etc.)</li>
          </ul>

          <h3 className="text-lg font-medium mt-4 mb-2">Technical Information</h3>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li>Device information and browser type</li>
            <li>IP address and location data</li>
            <li>Usage patterns and app interactions</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3 flex items-center gap-2" style={{ color: '#A2DAD3' }}>
            <Eye size={20} />
            3. How We Use Your Information
          </h2>
          <p className="text-gray-700 mb-2">We use the collected information to:</p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li>Provide and maintain the health tracking service</li>
            <li>Create and manage your user account</li>
            <li>Store and display your health records</li>
            <li>Generate health reports and analytics</li>
            <li>Enable sharing features with authorized caregivers</li>
            <li>Process subscription payments</li>
            <li>Send important notifications about your account</li>
            <li>Improve and optimize our services</li>
            <li>Respond to your inquiries and support requests</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3 flex items-center gap-2" style={{ color: '#A2DAD3' }}>
            <Share2 size={20} />
            4. Information Sharing
          </h2>
          <p className="text-gray-700 mb-4">
            We do <strong>NOT</strong> sell, trade, or rent your personal or health information to third parties. 
            We may share your information only in the following circumstances:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li><strong>With Your Consent:</strong> When you explicitly authorize sharing with other caregivers</li>
            <li><strong>Service Providers:</strong> With trusted third-party services that help us operate (e.g., cloud hosting, payment processing)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>5. Data Security</h2>
          <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#f0f9f8', border: '1px solid #A2DAD3' }}>
            <p className="text-gray-700 m-0">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
              <li>Encrypted data transmission (HTTPS/SSL)</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>JWT-based authentication</li>
              <li>Regular security audits</li>
              <li>Secure cloud infrastructure</li>
            </ul>
          </div>
          <p className="text-gray-700 mb-4">
            While we strive to protect your information, no method of electronic storage or transmission is 100% secure. 
            We cannot guarantee absolute security but are committed to protecting your data to the best of our ability.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>6. Data Retention</h2>
          <p className="text-gray-700 mb-4">
            We retain your personal and health information for as long as your account is active or as needed to 
            provide you services. You may request deletion of your data at any time through the app settings.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3 flex items-center gap-2" style={{ color: '#A2DAD3' }}>
            <Trash2 size={20} />
            7. Your Rights
          </h2>
          <p className="text-gray-700 mb-2">You have the right to:</p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li><strong>Access:</strong> View all data we have collected about you</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and all associated data</li>
            <li><strong>Export:</strong> Download your health records in PDF format</li>
            <li><strong>Revoke Access:</strong> Remove shared access permissions at any time</li>
            <li><strong>Opt-out:</strong> Unsubscribe from non-essential communications</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>8. Children's Privacy</h2>
          <p className="text-gray-700 mb-4">
            Annie Cares is designed for adult caregivers. While the app may be used to track health information for 
            minors under the caregiver's supervision, we do not knowingly collect personal information directly from 
            children under 13. If you believe we have collected information from a child, please contact us immediately.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>9. Cookies and Tracking</h2>
          <p className="text-gray-700 mb-4">
            We use essential cookies and local storage to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li>Keep you logged in to your account</li>
            <li>Remember your preferences</li>
            <li>Ensure the security of your session</li>
          </ul>
          <p className="text-gray-700 mb-4">
            We do not use tracking cookies for advertising purposes.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>10. Third-Party Services</h2>
          <p className="text-gray-700 mb-4">
            Our app may contain links to third-party websites or services. We are not responsible for the privacy 
            practices of these external sites. We encourage you to review their privacy policies before providing 
            any personal information.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>11. International Data Transfer</h2>
          <p className="text-gray-700 mb-4">
            Your information may be transferred to and processed in countries other than your own. We ensure that 
            appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>12. Changes to This Policy</h2>
          <p className="text-gray-700 mb-4">
            We may update this Privacy Policy from time to time. We will notify you of any material changes by 
            posting the new policy on this page and updating the "Last updated" date. We encourage you to review 
            this policy periodically.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>13. Contact Us</h2>
          <p className="text-gray-700 mb-4">
            If you have questions or concerns about this Privacy Policy or our data practices, please contact us 
            through the app or reach out to our support team.
          </p>

          <div className="mt-8 pt-6 border-t text-center">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-primary px-8"
            >
              I Understand
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
